// Please keep this in sync with issueKeys.js (for server)

import { TrackerDataType } from './constants';

export enum IssueAndServiceSharedValues {
  date = 'Date',
  entryDate = 'Entry Date',
  dueDate = 'Due Date',
  type = 'Type',
  unit = 'Unit',
  department = 'Department',
  leadExpert = 'Lead/Expert',
  audience = 'Audience',
  campaign = 'Campaign',
  backgroundResponse = 'Background/Response',
  status = 'Status',
  teamMembers = 'Team Members',
  keyMessaging = 'Key Messaging',
  autotracker = 'AutoTracker',
}

export enum IssueAndEngagementSharedValues {
  linkedService = 'Linked Service',
  contactMethod = 'Contact',
  topic = 'Topic',
  fieldAddons = 'Field Addons',
  tone = 'Tone',
}

// For mapping issues to flattened issues
export enum IssueKeys {
  date = IssueAndServiceSharedValues.date,
  entryDate = IssueAndServiceSharedValues.entryDate,
  dueDate = IssueAndServiceSharedValues.dueDate,
  linkedService = IssueAndEngagementSharedValues.linkedService,
  type = IssueAndServiceSharedValues.type,
  contactMethod = IssueAndEngagementSharedValues.contactMethod,
  topic = IssueAndEngagementSharedValues.topic,
  fieldAddons = IssueAndEngagementSharedValues.fieldAddons,
  units = IssueAndServiceSharedValues.unit,
  dept = IssueAndServiceSharedValues.department,
  expert = IssueAndServiceSharedValues.leadExpert,
  campaign = IssueAndServiceSharedValues.campaign,
  outlet = 'Outlet',
  journalist = 'Journalist',
  tone = IssueAndEngagementSharedValues.tone,
  response = IssueAndServiceSharedValues.backgroundResponse,
  status = IssueAndServiceSharedValues.status,
  lead = IssueAndServiceSharedValues.teamMembers,
  keyMessaging = IssueAndServiceSharedValues.keyMessaging,
  file = 'Story Link',
  autotracker = IssueAndServiceSharedValues.autotracker,
}

// For mapping services to flattened services
export enum ServiceKeys {
  date = IssueAndServiceSharedValues.date,
  entryDate = IssueAndServiceSharedValues.entryDate,
  dueDate = IssueAndServiceSharedValues.dueDate,
  linkedInteraction = 'Media Interactions',
  linkedEngagement = 'Engagements',
  linkedContentLog = 'Contents',
  type = IssueAndServiceSharedValues.type,
  service = 'Service',
  units = IssueAndServiceSharedValues.unit,
  dept = IssueAndServiceSharedValues.department,
  unitLead = IssueAndServiceSharedValues.leadExpert,
  campaign = IssueAndServiceSharedValues.campaign,
  complexity = 'Complexity',
  // temporarily hidden
  // diversity = 'Diversity',
  actions = IssueAndServiceSharedValues.backgroundResponse,
  status = IssueAndServiceSharedValues.status,
  teamMember = IssueAndServiceSharedValues.teamMembers,
  keyMessaging = IssueAndServiceSharedValues.keyMessaging,
  file = 'Comms Material',
  autotracker = IssueAndServiceSharedValues.autotracker,
}

// For mapping engagements to flattened engagements
export enum EngagementKeys {
  date = IssueAndServiceSharedValues.date,
  entryDate = IssueAndServiceSharedValues.entryDate,
  dueDate = IssueAndServiceSharedValues.dueDate,
  linkedService = IssueAndEngagementSharedValues.linkedService,
  type = IssueAndServiceSharedValues.type,
  contactMethod = IssueAndEngagementSharedValues.contactMethod,
  topic = IssueAndEngagementSharedValues.topic,
  fieldAddons = IssueAndEngagementSharedValues.fieldAddons,
  units = IssueAndServiceSharedValues.unit,
  dept = IssueAndServiceSharedValues.department,
  expert = IssueAndServiceSharedValues.leadExpert,
  campaign = IssueAndServiceSharedValues.campaign,
  organization = 'Organization',
  communityMember = 'Community Member',
  tone = IssueAndEngagementSharedValues.tone,
  background = 'Background Information',
  status = IssueAndServiceSharedValues.status,
  lead = IssueAndServiceSharedValues.teamMembers,
  notes = 'Notes/Details',
  file = 'Documentation',
}

export enum ContentLogKeys {
  entryDate = 'Entry Date',
  dueDate = 'Due Date',
  topic = 'Topic',
  type = 'Type',
  units = 'Unit',
  expert = 'Lead/Expert',
  audience = 'Audience',
  campaign = 'Campaign',
  lead = 'Team Members',
  status = 'Status',
  dept = 'Department',
  content = 'Content',
  background = 'Background Information',
  otherNotes = 'Other Notes',
  file = 'Drafts, Images & Links',
  liveLink = 'Live Link',
  linkedService = 'Linked Service',
}

export const getQueryAdderKeysByEventHandlerType = (eventHandlerType: string) => {
  let keys;
  if (eventHandlerType === TrackerDataType.MEDIA_INTERACTION) {
    keys = IssueKeys;
  } else if (eventHandlerType === TrackerDataType.ENGAGEMENT) {
    keys = EngagementKeys;
  } else if (eventHandlerType === TrackerDataType.CONTENT_LOG) {
    keys = ContentLogKeys;
  } else {
    keys = ServiceKeys;
  }
  return keys;
};

export const queryAdderMediaFields = {
  topic: 'topic',
  entryDate: 'entryDate',
  dueDate: 'dueDate',
  linkedService: 'linkedService',
  type: 'type',
  units: 'units',
  expert: 'expert',
  dept: 'department',
  campaign: 'campaign',
  outlet: 'outlet',
  lead: 'lead',
  journalist: 'journalist',
  tone: 'tone',
  contactMethod: 'contactMethod',
  status: 'status',
  response: 'response',
  keyMessaging: 'keyMessaging',
  file: 'file',
};

export const queryAdderServiceKeys = {
  service: 'service',
  entryDate: 'entryDate',
  dueDate: 'dueDate',
  // temporarily hidden
  // diversity: 'diversity',
  complexity: 'complexity',
  type: 'type',
  units: 'units',
  unitLead: 'unitLead',
  dept: 'dept',
  campaign: 'campaign',
  teamMember: 'teamMember',
  status: 'status',
  file: 'file',
  actions: 'actions',
  keyMessaging: 'keyMessaging',
  linkedInteraction: 'linkedInteraction',
  linkedEngagement: 'linkedEngagement',
};

export const queryAdderEngagementFields = {
  topic: 'topic',
  entryDate: 'entryDate',
  dueDate: 'dueDate',
  linkedService: 'linkedService',
  type: 'type',
  units: 'units',
  expert: 'expert',
  dept: 'department',
  campaign: 'campaign',
  organization: 'organization',
  lead: 'lead',
  communityMember: 'communityMember',
  tone: 'tone',
  contactMethod: 'contactMethod',
  status: 'status',
  background: 'response',
  notes: 'keyMessaging',
  file: 'file',
};

export const queryAdderContentLogFields = {
  topic: 'topic',
  entryDate: 'entryDate',
  dueDate: 'dueDate',
  linkedService: 'linkedService',
  type: 'type',
  units: 'units',
  expert: 'expert',
  dept: 'dept',
  campaign: 'campaign',
  audience: 'audience',
  lead: 'lead',
  status: 'status',
  otherNotes: 'otherNotes',
  content: 'content',
  background: 'background',
  file: 'file',
  liveLink: 'liveLink',
};

const nonBulkEditQueryAdderFields = new Set([
  'linkedService',
  'linkedInteraction',
  'linkedEngagement',
  'file',
  'liveLink',
]);

export const getQueryAdderColumnsByEventHandlerType = (eventHandlerType: string) => {
  let fields;
  if (eventHandlerType === TrackerDataType.MEDIA_INTERACTION) {
    fields = queryAdderMediaFields;
  } else if (eventHandlerType === TrackerDataType.ENGAGEMENT) {
    fields = queryAdderEngagementFields;
  } else if (eventHandlerType === TrackerDataType.CONTENT_LOG) {
    fields = queryAdderContentLogFields;
  } else {
    fields = queryAdderServiceKeys;
  }
  return Object.keys(fields);
};

export const getBulkEditQueryAdderColumnsByEventHandlerType = (eventHandlerType: string) => {
  const queryAdderCols = getQueryAdderColumnsByEventHandlerType(eventHandlerType);
  return queryAdderCols.filter((col) => !nonBulkEditQueryAdderFields.has(col));
};

// For custom analytics' 'Media Interactions In Progress' chart
export const inProgressKeys = {
  topic: IssueKeys.topic,
  outlet: 'Media Outlet',
  // we map this to IssueKeys.lead instead of IssueAndServiceSharedValues.teamMembers to emphasize the fact that
  // inProgressKeys in the context of issues only
  teamMember: IssueKeys.lead,
  timeStamp: 'Time Stamp',
};

export const TextInputKeys = [
  IssueAndEngagementSharedValues.topic,
  IssueAndServiceSharedValues.backgroundResponse,
  ServiceKeys.service,
  IssueAndServiceSharedValues.keyMessaging,
  EngagementKeys.background,
  EngagementKeys.notes,
  ContentLogKeys.content,
  ContentLogKeys.background,
  ContentLogKeys.otherNotes,
];

export const DisplayKeys = {
  ...IssueKeys,
  ...ServiceKeys,
  ...EngagementKeys,
  ...ContentLogKeys,
};
